import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { SwipeModal } from '@hqo/react-components-library/dist/molecules/modals/swipe-modal';
import { Button } from 'components/payment/quick-checkout/components/button/button';
import { MultidayQuickCheckoutContent } from 'components/payment/quick-checkout/components/multiday-checkout-content/multidayQuickCheckoutContent';
import { useDispatch, useSelector } from 'react-redux';
import { selectCurrentLocationId } from 'store/app-instance-configs/selectors';
import { selectCart, selectCartDiscountSummary, selectCartId, selectSubmitCartStatus } from 'store/cart/selectors';
import { getCart, submitCart } from 'store/cart/actions';
import { useSearchParams } from 'hooks/use-search-params.hook';
import { searchParams } from 'shared/types/search-params';
import { track } from '@hqo/web-tracking';
import { TRACK_EVENT_NAMES, TRACK_EVENT_TYPES } from 'shared/consts';
import { formatSummaryValues } from 'utils/formatSummaryValues';
import { formatCartItemsIds } from 'utils/formatTrackEventProps';
import { useIsSmallViewportWidth } from 'hooks/use-is-small-viewport-width.hook';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { selectResource } from 'store/resource/selectors';
import { useAddingVisitorsBlock } from 'components/adding-visitors-block/hooks/use-adding-visitors-block.hook';
import { StyledModalWrapper } from './styles';
import { useQuickCheckoutHeight } from '../../useQuickCheckoutHeight.hook';
import { MultidayQuickCheckoutDesktopContent } from './multidayQuickCheckoutDesktopContent';

export interface MultidayCheckoutContainerProps {
  reverseAnimation: boolean;
  closeQuickCheckoutHandler: VoidFunction;
}

export const MultidayCheckoutContainer = ({
  reverseAnimation,
  closeQuickCheckoutHandler,
}: MultidayCheckoutContainerProps) => {
  const intl = useIntl();
  const dispatch = useDispatch();

  const { locationId: currentLocationId } = useSearchParams<searchParams>();

  const locationId = useSelector(selectCurrentLocationId) || currentLocationId;
  const cart_id = useSelector(selectCartId);
  const submitCartStatus = useSelector(selectSubmitCartStatus);
  const cart = useSelector(selectCart);
  const discountSummary = useSelector(selectCartDiscountSummary);
  const isMobileDevice = useIsSmallViewportWidth();
  const quickCheckoutHeight = useQuickCheckoutHeight();

  const resource = useSelector(selectResource);
  const { showResourceBookingResourceTermsAndConditions } = useFlags();
  const [isTermsChecked, setIsTermsChecked] = useState(false);
  const { formattedAttendees, errorConfig } = useAddingVisitorsBlock();

  const isTermsVisible = useMemo(
    () => showResourceBookingResourceTermsAndConditions && !!resource.terms_and_conditions,
    [showResourceBookingResourceTermsAndConditions, resource],
  );
  const isAllowedVisitorsExist = Boolean(resource?.allowed_visitors);

  const checkoutDisabled = useMemo(
    () => (isTermsVisible && !isTermsChecked) || (Boolean(formattedAttendees?.length) && errorConfig.hasError),
    [isTermsVisible, isTermsChecked, formattedAttendees, errorConfig.hasError],
  );

  useEffect(() => {
    dispatch(getCart.request(cart_id));
  }, []);

  const onCTAClick = useCallback(() => {
    const { total, currencyType } = formatSummaryValues(cart?.total_summary);
    const cartItemsIds = formatCartItemsIds(cart);

    track(
      TRACK_EVENT_NAMES.CHECKOUT_CLICK,
      {
        type: TRACK_EVENT_TYPES.ACTION,
        cart_total: total,
        currency_code: currencyType,
        promo_code_applied: !!discountSummary,
        cart_type: cart?.type,
        items: cartItemsIds,
        adapter: cart?.config?.config?.adapter,
      },
      { sendToHqoTracking: true },
    );
    dispatch(
      submitCart.request({
        cart_id,
        locationId,
        ...(Boolean(formattedAttendees?.length) && { attendees: formattedAttendees }),
      }),
    );
  }, [cart_id, locationId, cart, discountSummary, formattedAttendees]);

  return isMobileDevice ? (
    <SwipeModal
      title={intl.formatMessage({ id: 'checkout' })}
      reverseAnimation={reverseAnimation}
      content={
        <MultidayQuickCheckoutContent
          isAllowedVisitorsExist={isAllowedVisitorsExist}
          isTermsVisible={isTermsVisible}
          isTermsChecked={isTermsChecked}
          onIsTermsChecked={setIsTermsChecked}
          visitorError={errorConfig.genericError}
        />
      }
      onClose={closeQuickCheckoutHandler}
      withLeftExitButton
      footerContent={
        <Button
          isFreeCart
          submitCartStatus={submitCartStatus}
          onCTAClick={onCTAClick}
          checkoutDisabled={checkoutDisabled}
        />
      }
    />
  ) : (
    <StyledModalWrapper
      quickCheckoutHeight={quickCheckoutHeight}
      content={
        <MultidayQuickCheckoutDesktopContent
          onCTAClick={onCTAClick}
          closeQuickCheckoutHandler={closeQuickCheckoutHandler}
          isAllowedVisitorsExist={isAllowedVisitorsExist}
          isTermsVisible={isTermsVisible}
          isTermsChecked={isTermsChecked}
          onIsTermsChecked={setIsTermsChecked}
          visitorError={errorConfig.genericError}
          checkoutDisabled={checkoutDisabled}
        />
      }
    />
  );
};
